import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, Clock, Wrench } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatError } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { useLocalizedService } from "@/lib/localizedService";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const STEP_KEYS = ["book.step.service", "book.step.date", "book.step.vehicle", "book.step.contact", "book.step.review"];

function StepIndicator({ current, t }) {
  return (
    <div className="mb-12 flex items-center gap-2">
      {STEP_KEYS.map((key, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={key} className="flex flex-1 items-center gap-2">
            <div
              className={`flex h-8 w-8 items-center justify-center border text-xs font-bold ${
                done
                  ? "border-[#FF4B00] bg-[#FF4B00] text-white"
                  : active
                    ? "border-[#FF4B00] text-[#FF4B00]"
                    : "border-white/20 text-white/40"
              }`}
            >
              {done ? <Check className="h-4 w-4" strokeWidth={3} /> : i + 1}
            </div>
            <span
              className={`mono hidden text-[10px] uppercase tracking-[0.2em] md:inline ${
                active ? "text-white" : "text-white/40"
              }`}
            >
              {t(key)}
            </span>
            {i < STEP_KEYS.length - 1 && (
              <div
                className={`h-px flex-1 ${done ? "bg-[#FF4B00]" : "bg-white/10"}`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function BookingWizard() {
  const navigate = useNavigate();
  const t = useT();
  const localizeService = useLocalizedService();
  const [step, setStep] = useState(0);
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [dateObj, setDateObj] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [vehicle, setVehicle] = useState({
    make: "",
    model: "",
    year: "",
    plate: "",
  });
  const [contact, setContact] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api
      .get("/services")
      .then((r) => setServices(r.data))
      .catch((e) => toast.error(formatError(e)));
  }, []);

  const dateStr = useMemo(() => {
    if (!dateObj) return "";
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, "0");
    const d = String(dateObj.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [dateObj]);

  useEffect(() => {
    if (!service || !dateStr) return;
    setSlotsLoading(true);
    setTime("");
    api
      .get("/availability", { params: { date_str: dateStr, service_id: service.id } })
      .then((r) => setSlots(r.data.slots || []))
      .catch((e) => toast.error(formatError(e)))
      .finally(() => setSlotsLoading(false));
  }, [service, dateStr]);

  const canNext = () => {
    if (step === 0) return !!service;
    if (step === 1) return !!dateStr && !!time;
    if (step === 2)
      return vehicle.make && vehicle.model && vehicle.year && vehicle.plate;
    if (step === 3) return contact.name && contact.email && contact.phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: service.id,
        date: dateStr,
        time,
        customer_name: contact.name,
        customer_email: contact.email,
        customer_phone: contact.phone,
        vehicle_make: vehicle.make,
        vehicle_model: vehicle.model,
        vehicle_year: parseInt(vehicle.year, 10),
        license_plate: vehicle.plate,
        notes: contact.notes,
      };
      const r = await api.post("/bookings", payload);
      toast.success("Anfrage gesendet – wir bestätigen kurz per Mail");
      navigate(`/buchung-erfolgreich/${r.data.reference}`);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="booking-wizard" className="min-h-screen">
      <Header />

      <div className="mx-auto max-w-4xl px-6 py-16 lg:px-10 lg:py-24">
        <div className="mb-10">
          <span className="tag-orange">{t("book.tag")}</span>
          <h1 className="mt-6 text-4xl font-black uppercase sm:text-5xl">
            {t("book.title")}
          </h1>
          <p className="mt-3 text-sm text-white/60">
            {t("book.subtitle")}
          </p>
        </div>

        <StepIndicator current={step} t={t} />

        <div className="border border-white/10 bg-[#0d0d10] p-6 sm:p-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.25 }}
            >
              {step === 0 && (
                <div data-testid="step-service">
                  <h2 className="mb-6 text-xl font-black uppercase">
                    {t("book.chooseService")}
                  </h2>
                  <div className="grid gap-3">
                    {services.map((rawS) => {
                      const s = localizeService(rawS);
                      const selected = service?.id === s.id;
                      return (
                        <button
                          key={s.id}
                          data-testid={`select-service-${s.id}`}
                          onClick={() => setService(rawS)}
                          className={`flex items-start justify-between border p-5 text-left transition-colors ${
                            selected
                              ? "border-[#FF4B00] bg-[#FF4B00]/5"
                              : "border-white/10 hover:border-white/30"
                          }`}
                        >
                          <div className="flex gap-4">
                            <Wrench
                              className={`h-6 w-6 flex-shrink-0 ${selected ? "text-[#FF4B00]" : "text-white/50"}`}
                              strokeWidth={1.5}
                            />
                            <div>
                              <div className="font-bold uppercase">{s.name}</div>
                              <div className="mt-1 text-sm text-white/60">
                                {s.description}
                              </div>
                              <div className="mono mt-2 text-[11px] uppercase tracking-widest text-white/40">
                                <Clock className="mr-1 inline h-3 w-3" strokeWidth={1.5} />
                                {s.duration_minutes} {t("services.min")}
                              </div>
                            </div>
                          </div>
                          <div className="mono text-right text-xl font-bold text-white">
                            {s.price_eur.toFixed(2).replace(".", ",")} €
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {step === 1 && (
                <div data-testid="step-date">
                  <h2 className="mb-6 text-xl font-black uppercase">
                    {t("book.dateTime")}
                  </h2>
                  <div className="grid gap-8 md:grid-cols-2">
                    <div className="border border-white/10 bg-black/40 p-4">
                      <Calendar
                        mode="single"
                        selected={dateObj}
                        onSelect={setDateObj}
                        disabled={(d) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          if (d < today) return true;
                          const wd = d.getDay();
                          return wd === 0 || wd === 6;
                        }}
                        className="mx-auto"
                      />
                    </div>
                    <div>
                      <div className="mono mb-3 text-[11px] uppercase tracking-[0.2em] text-white/50">
                        {t("book.slots.available")}
                      </div>
                      {!dateStr && (
                        <p className="text-sm text-white/50">
                          {t("book.slots.pickDate")}
                        </p>
                      )}
                      {dateStr && slotsLoading && (
                        <p className="text-sm text-white/50">{t("book.slots.loading")}</p>
                      )}
                      {dateStr && !slotsLoading && slots.length === 0 && (
                        <p className="text-sm text-white/50">
                          {t("book.slots.none")}
                        </p>
                      )}
                      {slots.length > 0 && (
                        <div className="grid grid-cols-3 gap-2">
                          {slots.map((s) => (
                            <button
                              key={s}
                              data-testid={`slot-${s}`}
                              onClick={() => setTime(s)}
                              className={`mono border px-2 py-3 text-sm transition-colors ${
                                time === s
                                  ? "border-[#FF4B00] bg-[#FF4B00] text-white"
                                  : "border-white/10 text-white hover:border-white/40"
                              }`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div data-testid="step-vehicle">
                  <h2 className="mb-6 text-xl font-black uppercase">
                    {t("book.vehicle")}
                  </h2>
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.make")}
                      </Label>
                      <Input
                        data-testid="vehicle-make"
                        className="mt-2 border-white/20 bg-black text-white"
                        placeholder="z.B. Volkswagen"
                        value={vehicle.make}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, make: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.model")}
                      </Label>
                      <Input
                        data-testid="vehicle-model"
                        className="mt-2 border-white/20 bg-black text-white"
                        placeholder="z.B. Golf VII"
                        value={vehicle.model}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, model: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.year")}
                      </Label>
                      <Input
                        data-testid="vehicle-year"
                        type="number"
                        className="mono mt-2 border-white/20 bg-black text-white"
                        placeholder="2020"
                        value={vehicle.year}
                        onChange={(e) =>
                          setVehicle({ ...vehicle, year: e.target.value })
                        }
                      />
                    </div>
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.plate")}
                      </Label>
                      <Input
                        data-testid="vehicle-plate"
                        className="mono mt-2 border-white/20 bg-black uppercase text-white"
                        placeholder="B-XX 1234"
                        value={vehicle.plate}
                        onChange={(e) =>
                          setVehicle({
                            ...vehicle,
                            plate: e.target.value.toUpperCase(),
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div data-testid="step-contact">
                  <h2 className="mb-6 text-xl font-black uppercase">{t("book.contact")}</h2>
                  <div className="grid gap-5">
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.name")}
                      </Label>
                      <Input
                        data-testid="contact-name"
                        className="mt-2 border-white/20 bg-black text-white"
                        value={contact.name}
                        onChange={(e) =>
                          setContact({ ...contact, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                          {t("book.email")}
                        </Label>
                        <Input
                          data-testid="contact-email"
                          type="email"
                          className="mono mt-2 border-white/20 bg-black text-white"
                          value={contact.email}
                          onChange={(e) =>
                            setContact({ ...contact, email: e.target.value })
                          }
                        />
                      </div>
                      <div>
                        <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                          {t("book.phone")}
                        </Label>
                        <Input
                          data-testid="contact-phone"
                          className="mono mt-2 border-white/20 bg-black text-white"
                          value={contact.phone}
                          onChange={(e) =>
                            setContact({ ...contact, phone: e.target.value })
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="mono text-[11px] uppercase tracking-widest text-white/60">
                        {t("book.notes")}
                      </Label>
                      <Textarea
                        data-testid="contact-notes"
                        className="mt-2 border-white/20 bg-black text-white"
                        placeholder={t("book.notesPlaceholder")}
                        value={contact.notes}
                        onChange={(e) =>
                          setContact({ ...contact, notes: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div data-testid="step-review">
                  <h2 className="mb-6 text-xl font-black uppercase">
                    {t("book.review")}
                  </h2>
                  <div className="divide-y divide-white/10 border border-white/10 bg-black/30">
                    <Row label={t("book.row.service")} value={localizeService(service)?.name} />
                    <Row
                      label={t("book.row.price")}
                      value={`${service?.price_eur.toFixed(2).replace(".", ",")} €`}
                    />
                    <Row
                      label={t("book.row.appt")}
                      value={`${dateStr} · ${time}`}
                      mono
                    />
                    <Row
                      label={t("book.row.vehicle")}
                      value={`${vehicle.make} ${vehicle.model} (${vehicle.year})`}
                    />
                    <Row label={t("book.row.plate")} value={vehicle.plate} mono />
                    <Row label={t("book.row.customer")} value={contact.name} />
                    <Row label={t("book.row.email")} value={contact.email} mono />
                    <Row label={t("book.row.phone")} value={contact.phone} mono />
                    {contact.notes && (
                      <Row label={t("book.row.notes")} value={contact.notes} />
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Actions */}
          <div className="mt-10 flex items-center justify-between border-t border-white/10 pt-6">
            <button
              data-testid="wizard-back"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 text-xs font-bold uppercase tracking-widest transition-colors hover:border-white disabled:opacity-30"
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
              {t("book.back")}
            </button>
            {step < STEP_KEYS.length - 1 ? (
              <button
                data-testid="wizard-next"
                onClick={() => setStep((s) => s + 1)}
                disabled={!canNext()}
                className="inline-flex items-center gap-2 bg-[#FF4B00] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E64300] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {t("book.next")}
                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
              </button>
            ) : (
              <button
                data-testid="wizard-submit"
                onClick={submit}
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-[#FF4B00] px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-[#E64300] disabled:opacity-40"
              >
                {submitting ? t("book.submitting") : t("book.submit")}
                <Check className="h-4 w-4" strokeWidth={2.5} />
              </button>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-4 p-5">
      <span className="mono text-[11px] uppercase tracking-[0.2em] text-white/50">
        {label}
      </span>
      <span
        className={`text-right text-sm text-white ${mono ? "mono" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}
